import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { EnvelopeSimple, CheckCircle } from '@phosphor-icons/react';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';

/** Banner di verifica email con codice a 6 cifre (mostrato finché l'email non è confermata). */
export const EmailVerificationBanner = () => {
  const { user, checkAuth } = useAuth();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  if (!user || !['user', 'professional'].includes(user.role) || user.email_verified) return null;

  const verify = async () => {
    if (code.trim().length < 4) return toast.error('Inserisci il codice ricevuto per email');
    setLoading(true);
    try {
      await api.post('/api/auth/verify-email', { code: code.trim() });
      toast.success('Email confermata, grazie!');
      setCode('');
      await checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Codice non valido');
    } finally { setLoading(false); }
  };

  const resend = async () => {
    setLoading(true);
    try {
      const res = await api.post('/api/auth/send-verification-code');
      setCooldown(res.data?.resend_after_seconds || 60);
      toast.success(`Nuovo codice inviato a ${user.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Non è stato possibile inviare il codice');
    } finally { setLoading(false); }
  };

  return (
    <div
      className="rounded-2xl border border-amber-300/60 bg-amber-50 p-5 flex flex-col md:flex-row md:items-center gap-4"
      data-testid="email-verification-banner"
    >
      <EnvelopeSimple size={28} weight="duotone" className="text-amber-600 shrink-0" />
      <div className="flex-1">
        <p className="font-heading font-medium text-foreground">Conferma il tuo indirizzo email</p>
        <p className="text-sm text-foreground/70 mt-0.5">
          Abbiamo inviato un codice a 6 cifre a <strong>{user.email}</strong>. Inseriscilo per attivare tutte le notifiche del tuo account.
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          inputMode="numeric"
          className="w-28 text-center tracking-[0.3em] font-mono bg-white"
          data-testid="verify-code-input"
        />
        <Button onClick={verify} disabled={loading} data-testid="verify-code-btn">
          <CheckCircle size={16} className="mr-1.5" /> Conferma
        </Button>
        <Button variant="ghost" onClick={resend} disabled={loading || cooldown > 0} data-testid="resend-code-btn">
          {cooldown > 0 ? `Rinvia (${cooldown}s)` : 'Rinvia codice'}
        </Button>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
